import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComercialLayout } from '../../components/comercial/ComercialLayout';
import { StageBadge, TipoClienteIcon, LoadingState, EmptyState, FilterChip } from '../../components/comercial/shared';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Beaker, Plus, Calendar, Timer } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { id: 'todas', label: 'Todas' },
  { id: 'pendientes', label: 'Pendientes de instalación' },
  { id: 'activas', label: 'Activas' },
  { id: 'finalizadas', label: 'Finalizadas' },
];

export default function PruebasPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trials, setTrials] = useState([]);
  const [filter, setFilter] = useState('todas');

  const fetchTrials = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/comercial/trials`, { withCredentials: true });
      // Solo registros que han entrado en el flujo de prueba
      const pruebas = (response.data || []).filter((t) =>
        ['pendiente_instalacion_prueba', 'prueba_activa'].includes(t.crm_stage) ||
        (t.fecha_instalacion && ['pendiente_contrato', 'cliente', 'pendiente_retirada', 'perdido'].includes(t.crm_stage))
      );
      setTrials(pruebas);
    } catch (err) {
      console.error('Error fetching pruebas:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrials(); }, [fetchTrials]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filtered = trials.filter((t) => {
    if (filter === 'pendientes') return t.crm_stage === 'pendiente_instalacion_prueba';
    if (filter === 'activas') return t.crm_stage === 'prueba_activa';
    if (filter === 'finalizadas') return !['pendiente_instalacion_prueba', 'prueba_activa'].includes(t.crm_stage);
    return true;
  });

  return (
    <ComercialLayout
      title="Pruebas Gratuitas"
      subtitle={`${trials.filter((t) => t.crm_stage === 'prueba_activa').length} activas · ${trials.filter((t) => t.crm_stage === 'pendiente_instalacion_prueba').length} pendientes`}
      onLogout={handleLogout}
      headerActions={
        <Button
          onClick={() => navigate('/comercial/nueva-prueba')}
          className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
          data-testid="pruebas-nueva-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Prueba
        </Button>
      }
    >
      <div className="space-y-4" data-testid="pruebas-page">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <FilterChip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} testId={`pruebas-filter-${f.id}`}>
              {f.label}
            </FilterChip>
          ))}
        </div>

        {loading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Beaker}
            title="Sin pruebas"
            description="No hay pruebas gratuitas en este estado."
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((t) => (
              <button
                key={t.id}
                onClick={() => navigate(`/comercial/cliente/${t.id}`)}
                className="p-5 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/20 text-left transition-colors"
                data-testid={`prueba-card-${t.client_code}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <TipoClienteIcon tipo={t.tipo_cliente} />
                    <div>
                      <p className="text-sm text-white font-medium">
                        {t.tipo_cliente === 'empresa' ? t.empresa : t.nombre_contacto}
                      </p>
                      <span className="text-xs font-mono text-zinc-500">{t.client_code}</span>
                    </div>
                  </div>
                  <StageBadge stage={t.crm_stage} label={t.stage_label} />
                </div>
                <div className="space-y-1.5 text-xs text-zinc-400">
                  {t.equipo_recomendado && t.equipo_recomendado !== 'pendiente_asesoramiento' && (
                    <p>Equipo: <span className="text-zinc-300">{t.equipo_recomendado}</span></p>
                  )}
                  {t.fecha_prevista && (
                    <p className="flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" />
                      {t.fecha_prevista}{t.hora_prevista ? ` · ${t.hora_prevista}` : ''}
                    </p>
                  )}
                  {t.crm_stage === 'prueba_activa' && t.dias_en_prueba !== null && t.dias_en_prueba !== undefined && (
                    <p className="flex items-center gap-1.5 text-purple-300">
                      <Timer className="w-3 h-3" />
                      {t.dias_en_prueba} días en prueba
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </ComercialLayout>
  );
}
